import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withKeys as _withKeys, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createBlock as _createBlock, mergeProps as _mergeProps, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { id: "main-content" }
const _hoisted_2 = {
  key: 0,
  id: "introduction-overlay"
}
const _hoisted_3 = { id: "intro-window-close-button" }
const _hoisted_4 = { class: "intro-text" }
const _hoisted_5 = { style: {"color":"#f4ba3e"} }
const _hoisted_6 = { style: {"color":"#f4ba3e"} }
const _hoisted_7 = {
  class: "modal",
  id: "modal-loading"
}
const _hoisted_8 = { id: "top-content" }
const _hoisted_9 = { id: "left-buttons" }
const _hoisted_10 = { id: "location-info" }
const _hoisted_11 = { id: "geolocation-close" }
const _hoisted_12 = { id: "geolocation-controls" }
const _hoisted_13 = { id: "right-buttons" }
const _hoisted_14 = {
  id: "controls",
  class: "collapsable-control control-icon-wrapper"
}
const _hoisted_15 = { class: "controls-top-row" }
const _hoisted_16 = {
  key: 0,
  id: "control-checkboxes"
}
const _hoisted_17 = {
  id: "planet-visibility-box",
  class: "collapsable-control control-icon-wrapper"
}
const _hoisted_18 = { class: "controls-top-row planet-visibility" }
const _hoisted_19 = {
  key: 0,
  id: "planet-visibility-label"
}
const _hoisted_20 = { id: "bottom-content" }
const _hoisted_21 = { id: "date-picker" }
const _hoisted_22 = { id: "change-flags" }
const _hoisted_23 = {
  key: 0,
  id: "body-logos"
}
const _hoisted_24 = { class: "video-wrapper" }
const _hoisted_25 = { id: "user-experience-footer" }

import { ref, reactive, computed, markRaw, onMounted, nextTick, watch } from "vue";
import { Color, Grids, Planets, Settings, WWTControl, AstroCalc } from "@wwtelescope/engine";
import { SolarSystemObjects } from "@wwtelescope/engine-types";
import { engineStore } from "@wwtelescope/engine-pinia";
import { BackgroundImageset, LocationDeg, skyBackgroundImagesets, supportsTouchscreen, blurActiveElement, useWWTKeyboardControls, API_BASE_URL, D2R } from "@cosmicds/vue-toolkit";
import { useDisplay } from "vuetify";
import { v4 } from "uuid";

import { useTimezone } from "./timezones";
import { horizontalToEquatorial, skyOpacityForSunAlt, getJulian, equatorialToHorizontal, type UserExperienceRating } from "./utils";
import { resetAltAzGridText, makeAltAzGridText, drawPlanets, renderOneFrame, drawEcliptic, drawSkyOverlays } from "./wwt-hacks";
import { MapBoxFeature, MapBoxFeatureCollection, geocodingInfoForSearch, textForLocation } from "@cosmicds/vue-toolkit/src/mapbox";
// import { useGeolocation } from "@cosmicds/vue-toolkit";
import { useSun } from './useSun';

const UUID_KEY = "eclipse-mini-uuid" as const;
const OPT_OUT_KEY = "eclipse-mini-optout" as const;
const RATING_OPT_OUT_KEY = "eclipse-mini-rating-optout" as const;
const SKIP_INTRO_CONTENT_KEY = "skip-intro-content" as const;
type SheetType = "text" | "video";
export interface PlanetParadeProps {
  wwtNamespace?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PlanetParade',
  props: {
    wwtNamespace: { default: "planet-parade" }
  },
  setup(__props: any) {

const STORY_DATA_URL = `${API_BASE_URL}/planet-parade/data`;
const STORY_RATING_URL = `${API_BASE_URL}/planet-parade/user-experience`;

const maybeUUID = window.localStorage.getItem(UUID_KEY);
const storedOptOut = window.localStorage.getItem(OPT_OUT_KEY);
const storedRatingOptOut = window.localStorage.getItem(RATING_OPT_OUT_KEY);
const skipIntroContent = window.localStorage.getItem(SKIP_INTRO_CONTENT_KEY)?.toLowerCase() === "true";
const existingUser = maybeUUID !== null;
const uuid = maybeUUID ?? v4();
const question = Math.random() > 0.5 ? 
  "Does this spark your curiosity?" :
  "Are you learning something new?";
const currentRating = ref<UserExperienceRating | null>(null);
const currentComments = ref<string | null>(null);
if (!existingUser) {
  window.localStorage.setItem(UUID_KEY, uuid);
}
let infoTimeMs = 0;
let videoPlayingTimeMs = 0;
let videoPlaying = false;
let videoOpened = false;
let videoPlayed = false;
let appStartTimestamp = Date.now();
let infoStartTimestamp = null as number | null;
let videoPlayingStartTimestamp = null as number | null;

let userSelectedSearchLocations: [number, number][] = [];
let userSelectedMapLocations: [number, number][] = [];

const store = engineStore();

useWWTKeyboardControls(store);

const touchscreen = supportsTouchscreen();
const { smAndDown, smAndUp, xs } = useDisplay();

const _props = __props;

const queryHideSplash = new URLSearchParams(window.location.search).get("splash")?.toLowerCase() === "false";
const splash = !(queryHideSplash || skipIntroContent);
const showSplashScreen = ref(splash);
const backgroundImagesets = reactive<BackgroundImageset[]>([]);
const sheet = ref<SheetType | null>(null);
const layersLoaded = ref(false);
const positionSet = ref(false);
const accentColor = ref("#f4ba3e");
const accentColor2 = ref("#6793ff");
const buttonColor = ref("#f4ba3e");
const tab = ref(0);
const showHorizon = ref(true);
const showAltAzGrid = ref(true);
const showEcliptic = ref(true);
const showLocationSelector = ref(false);
const playing = ref(false);
const showControls = ref(smAndUp.value);
const showConstellations = ref(false);
const showPlanetLabels = ref(true);
const inIntro = ref(false);
const showPrivacyDialog = ref(false);
const datePickerOpen = ref(false);
const skipIntroChecked = ref(skipIntroContent);
const showPlanetVisiblity = ref(true);

const wwtStats = markRaw({
  timeResetCount: 0,
  reverseCount: 0,
  playPauseCount: 0,
  speedups: [] as number[],
  slowdowns: [] as number[],
  rateSelections: [] as number[],
  startTime: Date.now(),
});

const optOut = typeof storedOptOut === "string" ? storedOptOut === "true" : null;
const responseOptOut = ref(optOut);

// If the user has opted out of all data collection, don't ask them the question
const ratingOptedOut = typeof storedRatingOptOut === "string" ? storedRatingOptOut === "true" : null;
const ratingOptOut = ref(responseOptOut.value || ratingOptedOut);
let ratingTimeout: ReturnType<typeof setTimeout> | null = null;

const showRating = ref(false);
const showWebGL2Dialog = ref(false);
const showRatingPrivacyPolicy = ref(false);

const geocodingOptions = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  access_token: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN ?? "", 
};

const selectedLocation = ref<LocationDeg>({
  longitudeDeg: -71.1056,
  latitudeDeg: 42.3581,
});
const selectedLocationText = ref("");
updateSelectedLocationText();

// const { geolocation, geolocate} = useGeolocation();
// function useGeolocated() {
//   if (!geolocation.value) {return;}
//   selectedLocation.value = { latitudeDeg: geolocation.value.latitude, longitudeDeg: geolocation.value.longitude };
// }
// watch(
//   geolocation,
//   (location) => {
//     if (location) {
//       selectedLocation.value = { latitudeDeg: location?.latitude, longitudeDeg: location?.longitude };
//     }
//   }
// );

const searchErrorMessage = ref<string | null>(null);
const { selectedTimezoneOffset, shortTimezone, browserTimezoneOffset } = useTimezone(selectedLocation);

const selectedTime = ref(Date.now());

const { getTimeforSunAlt, getSunPositionAtTime } = useSun(store, selectedLocation, selectedTime, selectedTimezoneOffset);
// faking localization because
// <date-time-picker> and <time-display> are not timezone aware
const localSelectedDate = computed({
  // if you console log this date it will still say the local timezone 
  // as determined by the browser Intl.DateTimeFormat().resolvedOptions().timeZone
  // but we have manually offset it so the hours are correct for the selected timezone
  get: () => {
    const time = selectedTime.value;
    const fakeUTC = time + browserTimezoneOffset;
    return new Date(fakeUTC + selectedTimezoneOffset.value);
  },
  set: (value: Date) => {
    // get local time
    const time = value.getTime();
    // undo fake localization
    const newTime = time - selectedTimezoneOffset.value - browserTimezoneOffset;
    selectedTime.value = new Date(newTime).getTime();
  }
});

function isWebGL2Enabled(): boolean {
  // It doesn't seem like there's a better way to do this than just to try and get a context
  // https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/By_example/Detect_WebGL
  // NB: The engine specifically wants a webgl2 context
  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl2");
  return gl instanceof WebGL2RenderingContext;
}

function clearRatingTimeout() {
  if (ratingTimeout !== null) {
    clearTimeout(ratingTimeout);
    ratingTimeout = null;
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const wwtSettings: Settings = Settings.get_active();

function doWWTModifications() {

  Grids._makeAltAzGridText = makeAltAzGridText;
  Grids.drawEcliptic = drawEcliptic;

  // We need to render one frame ahead of time
  // as there's a lot of setup done on the first frame
  // render that we need to use
  WWTControl.singleton.renderOneFrame();

  const boundRenderOneFrame = renderOneFrame.bind(WWTControl.singleton);
  const newFrameRender = function() { 
    boundRenderOneFrame(
      showHorizon.value,
      showHorizon.value,
      showPlanetLabels.value,
    );
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  WWTControl.singleton._drawSkyOverlays = drawSkyOverlays.bind(WWTControl.singleton);

  // as well as our custom text overlays
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  WWTControl.singleton.renderOneFrame = newFrameRender;

  const originalUpdatePlanetLocations = Planets.updatePlanetLocations;
  const planetScales = [
    8,  // Sun
    1.25,  // Mercury
    1.25,  // Venus
    1.25,  // Mars
    2.5,  // Jupiter
    4.5,  // Saturn
    2,  // Uranus
    2,  // Neptune
    1,  // Pluto
    1.25,  // Moon
  ];
  function newUpdatePlanetLocations(threeD: boolean) {
    originalUpdatePlanetLocations(threeD);
    for (let i = 0; i <= SolarSystemObjects.moon; i++) {
      Planets._planetScales[i] = planetScales[i];
    }
  }
  Planets.updatePlanetLocations = newUpdatePlanetLocations;
  Planets.drawPlanets = drawPlanets;

  // Recall that zoom = 6 * FOV, in WWT
  const maxFOV = 90;
  WWTControl.singleton.set_zoomMax(maxFOV * 6);

}


function planetIsVisible(planetName, date: Date, location: LocationDeg) {
  // Get planet position
  const planetPos = AstroCalc.getPlanet(getJulian(date), planetName, location.latitudeDeg * D2R, location.longitudeDeg * D2R, 0);
  // Convert to horizontal
  const altAz = equatorialToHorizontal(planetPos.RA * 15 * D2R, planetPos.dec * D2R, location.latitudeDeg * D2R, location.longitudeDeg * D2R, date);
  // Check if planet is above minimum altitude
  return altAz.altRad > 0;
}

const sunVis = computed(() => planetIsVisible(SolarSystemObjects.sun, dateTime.value, selectedLocation.value));
const mercuryVis = computed(() => planetIsVisible(SolarSystemObjects.mercury, dateTime.value, selectedLocation.value));
const venusVis = computed(() => planetIsVisible(SolarSystemObjects.venus, dateTime.value, selectedLocation.value));
const marsVis = computed(() => planetIsVisible(SolarSystemObjects.mars, dateTime.value, selectedLocation.value));
const jupiterVis = computed(() => planetIsVisible(SolarSystemObjects.jupiter, dateTime.value, selectedLocation.value));
const saturnVis = computed(() => planetIsVisible(SolarSystemObjects.saturn, dateTime.value, selectedLocation.value));
const uranusVis = computed(() => planetIsVisible(SolarSystemObjects.uranus, dateTime.value, selectedLocation.value));
const neptuneVis = computed(() => planetIsVisible(SolarSystemObjects.neptune, dateTime.value, selectedLocation.value));

onMounted(() => {
  store.waitForReady().then(async () => {

    if (!isWebGL2Enabled()) {
      closeSplashScreen();
      showWebGL2Dialog.value = true; 
      layersLoaded.value = true;
      positionSet.value = true;
      inIntro.value = false;
      // eslint-disable-next-lint @typescript-eslint/ban-ts-comment
      // @ts-expect-error `canvas` is defined
      WWTControl.singleton.canvas.setAttribute("hidden", "true");
      WWTControl.singleton.renderOneFrame = function() {};
      return;
    }

    skyBackgroundImagesets.forEach(iset => backgroundImagesets.push(iset));

    // If there are layers to set up, do that here!
    layersLoaded.value = true;

    const altTime = getTimeforSunAlt(2).setting;
    if (altTime) {
      selectedTime.value = altTime;
    }
    wwtStats.startTime = selectedTime.value;
    setTimeout(() => resetCamera().then(() => positionSet.value = true), 300);

    store.applySetting(["localHorizonMode", true]);
    store.applySetting(["altAzGridColor", Color.fromArgb(180, 133, 201, 254)]);
    store.applySetting(["eclipticColor", Color.fromArgb(255, 255, 0, 255)]);
    store.applySetting(["actualPlanetScale", false]);
    updateAltAzGrid(showAltAzGrid.value);
    updateAltAzGridText(showAltAzGrid.value || showHorizon.value);
    updateEcliptic(showEcliptic.value);
    updateConstellations(showConstellations.value);
    updateWWTLocation(selectedLocation.value);
    store.setClockSync(false);
    store.setClockRate(1800);

    doWWTModifications();
    // geolocate().then(() => {
    //   console.log('got location');
    //   useGeolocated(); 
    //   selectedTime.value = todayAt4pm.value;
    //   resetCamera();
    // });
    
    // doUserSetup();

    setInterval(() => {
      if (playing.value) {
        const time = store.currentTime;
        selectedTime.value = time.getTime();
      }
      const sunPosition = getSunPositionAtTime(store.currentTime);
      updateEclipticColor(sunPosition.altRad);
    }, 500);

    window.addEventListener("keyup", (event: KeyboardEvent) => {
      if (["Esc", "Escape"].includes(event.key) && showVideoSheet.value) {
        showVideoSheet.value = false;
      }
    });

    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        updateUserData();
      } else {
        resetData();
      }
    });
  });
});

const ready = computed(() => layersLoaded.value && positionSet.value);

/* `isLoading` is a bit redundant here, but it could potentially have independent logic */
const isLoading = computed(() => !ready.value);

// It doesn't really matter which one we note here
const inNorthernHemisphere = computed(() => selectedLocation.value.latitudeDeg > 0);

const dateTime = computed(() => new Date(selectedTime.value));

/* Properties related to device/screen characteristics */
const smallSize = computed(() => smAndDown.value);

const mobile = computed(() => smallSize.value && touchscreen);

/* This lets us inject component data into element CSS */
const cssVars = computed(() => {
  return {
    "--accent-color": accentColor.value,
    "--accent-color2": accentColor2.value,
    "--app-content-height": showTextSheet.value ? "66%" : "100%",
    // "--rating-width": smallSize.value ? "40%" : "40%",
  };
});


/**
  Computed flags that control whether the relevant dialogs display.
  The `sheet` data member stores which sheet is open, so these are just
  computed wrappers around modifying/querying that which can be used as
  dialog v-model values
*/
const showTextSheet = computed({
  get() {
    return sheet.value === "text";
  },
  set(_value: boolean) {
    selectSheet("text");
  }
});

const showVideoSheet = computed({
  get() {
    return sheet.value === "video";
  },
  set(value: boolean) {
    selectSheet("video");
    if (!value) {
      const video = document.querySelector("#info-video") as HTMLVideoElement;
      video.pause();
    } else {
      nextTick(() => {
        const video = document.querySelector("#info-video") as HTMLVideoElement;
        video.addEventListener("play", (_event: Event) => {
          videoPlaying = true;
          videoPlayingStartTimestamp = Date.now();
          videoPlayed = true;
        });

        video.addEventListener("pause", (_event: Event) => {
          videoPlaying = false;
          const now = Date.now();
          if (videoPlayingStartTimestamp !== null) {
            videoPlayingTimeMs += (now - videoPlayingStartTimestamp);
            videoPlayingStartTimestamp = null;
          }
        });
      });
    }
  }
});

/**
  This is convenient if there's any other logic that we want to run
  when the splash screen is closed
*/
function closeSplashScreen() {
  showSplashScreen.value = false;
}

function selectSheet(sheetType: SheetType | null) {
  if (sheet.value === sheetType) {
    sheet.value = null;
    nextTick(() => {
      blurActiveElement();
    });
  } else {
    sheet.value = sheetType;
  }
}

function getTextForLocation(longitudeDeg: number, latitudeDeg: number): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  return textForLocation(longitudeDeg, latitudeDeg, geocodingOptions);
}

function setLocationFromFeature(feature: MapBoxFeature) {
  selectedLocation.value = { longitudeDeg: feature.center[0], latitudeDeg: feature.center[1] };
  getTextForLocation(feature.center[0], feature.center[1]).then(text => {
    selectedLocationText.value = text;
  }).catch(_err => {
    searchErrorMessage.value = "An error occurred while searching";
  });
}

function setLocationFromSearchFeature(feature: MapBoxFeature) {
  setLocationFromFeature(feature);
  userSelectedSearchLocations.push(feature.center);
}

async function updateSelectedLocationText() {
  selectedLocationText.value = await getTextForLocation(selectedLocation.value.longitudeDeg, selectedLocation.value.latitudeDeg);
}

function searchProvider(text: string): Promise<MapBoxFeatureCollection> {
  return geocodingInfoForSearch(text, geocodingOptions);
}

function updateLocationFromMap(location: LocationDeg) {
  selectedLocation.value = location;
  userSelectedMapLocations.push([location.latitudeDeg, location.longitudeDeg]);
}

function updateUserExperienceInfo(rating: UserExperienceRating | null, comments: string | null) {
  const body: Record<string, unknown> = {
    uuid,
    question,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    story_name: "planet-parade",
  };
  if (rating) {
    body.rating = rating;
  }
  if (comments) {
    body.comments = comments;
  }
  fetch(STORY_RATING_URL, {
    method: "PUT",
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

async function _doUserSetup() {
  if (responseOptOut.value) {
    return;
  }

  const response = await fetch(`${STORY_DATA_URL}/${uuid}`, {
    method: "GET",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    headers: { "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "" },
  });
  const content = await response.json();
  const userExists = response.status === 200 && content.response?.user_uuid != undefined;

  if (!userExists) {
    fetch(`${STORY_DATA_URL}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
      },
      body: JSON.stringify({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        user_uuid: uuid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        user_selected_search_locations: userSelectedSearchLocations,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        user_selected_map_locations: userSelectedMapLocations,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        app_time_ms: 0, info_time_ms: 0, video_time_ms: 0,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        video_opened: videoOpened, video_played: videoPlayed,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        wwt_time_reset_count: wwtStats.timeResetCount,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        wwt_reverse_count: wwtStats.reverseCount,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        wwt_play_pause_count: wwtStats.playPauseCount,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        wwt_speedups: wwtStats.speedups,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        wwt_slowdowns: wwtStats.slowdowns,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        wwt_rate_selections: wwtStats.rateSelections,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        wwt_start_stop_times: [wwtStats.startTime, selectedTime.value],
      }),
    });
  }

  if (ratingOptOut.value) {
    return;
  }

  let gaveRating = false;
  if (userExists) {
    const ratingResponse = await fetch(`${STORY_RATING_URL}/${uuid}`, {
      method: "GET",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "" }
    });

    const ratingContent = await ratingResponse.json();
    gaveRating = ratingResponse.status === 200 && ratingContent.ratings?.length > 0;
  }

  if (!gaveRating) {
    ratingTimeout = setTimeout(() => {
      showRating.value = true; 
    }, 40_000);
  }
}

function resetData() {
  userSelectedMapLocations = [];
  userSelectedSearchLocations = [];
  infoTimeMs = 0;
  videoPlayingTimeMs = 0;
  const now = Date.now();
  appStartTimestamp = now;
  infoStartTimestamp = showTextSheet.value ? now : null;
  videoPlayingStartTimestamp = videoPlaying ? now : null;
  Object.assign(wwtStats, {
    timeResetCount: 0,
    reverseCount: 0,
    playPauseCount: 0,
    speedups: [],
    slowdowns: [],
    rateSelections: [],
    startTime: selectedTime.value,
  });
}

async function updateUserData() {
  if (responseOptOut.value) {
    return;
  }

  const now = Date.now();
  const infoTime = (showTextSheet.value && infoStartTimestamp !== null) ? now - infoStartTimestamp : infoTimeMs;
  const videoTime = (videoPlaying && videoPlayingStartTimestamp !== null) ? now - videoPlayingStartTimestamp : videoPlayingTimeMs;

  fetch(`${STORY_DATA_URL}/${uuid}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
    },
    body: JSON.stringify({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      user_selected_search_locations: userSelectedSearchLocations,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      user_selected_map_locations: userSelectedMapLocations,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_app_time_ms: now - appStartTimestamp,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_info_time_ms: infoTime,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_video_time_ms: videoTime,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      video_opened: videoOpened, video_played: videoPlayed,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_wwt_time_reset_count: wwtStats.timeResetCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_wwt_reverse_count: wwtStats.reverseCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_wwt_play_pause_count: wwtStats.playPauseCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      wwt_speedups: wwtStats.speedups,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      wwt_slowdowns: wwtStats.slowdowns,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      wwt_rate_selections: wwtStats.rateSelections,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      wwt_start_stop_times: [wwtStats.startTime, selectedTime.value],
    }),
    keepalive: true,
  }).then(() => {
    resetData();
  });
}

async function resetCamera(time?: Date): Promise<void> {
  if (time === undefined) {
    time = store.currentTime;
  }

  const latRad = selectedLocation.value.latitudeDeg * D2R;
  const lonRad = selectedLocation.value.longitudeDeg * D2R;

  const sunAltAz = getSunPositionAtTime(time);
  const sunAz = sunAltAz.azRad;
  const startAlt = smallSize.value ? 15 * D2R : 20 * D2R;
  const startRADec = horizontalToEquatorial(
    startAlt,
    sunAz,
    latRad,
    lonRad,
    time,
  );

  return store.gotoRADecZoom({
    raRad: startRADec.raRad,
    decRad: startRADec.decRad,
    zoomDeg: 360,
    instant: true,
  });
  
}

function updateWWTLocation(location: LocationDeg) {
  wwtSettings.set_locationLat(location.latitudeDeg);
  wwtSettings.set_locationLng(location.longitudeDeg);
}

function updateAltAzGrid(show: boolean) {
  store.applySetting(["showAltAzGrid", show]);
}

function updateAltAzGridText(show: boolean) {
  store.applySetting(["showAltAzGridText", show]);
}

function updateEcliptic(show: boolean) {
  store.applySetting(["showEcliptic", show]);
  // store.applySetting(["showEclipticOverviewText", show]);
}

function updateEclipticColor(sunAlt: number) {
  const opacity = skyOpacityForSunAlt(sunAlt);
  const component = 255 - opacity * (255 - 119);
  const color = Color.fromArgb(255, component, 0, component);
  store.applySetting(["eclipticColor", color]);
}

function updateConstellations(show: boolean) {
  store.applySetting(["showConstellationFigures", show]);
  store.applySetting(["showConstellationLabels", show]);
}

watch(selectedLocation, (location: LocationDeg) => {
  console.log('selectedLocation changed', location);
  updateSelectedLocationText();
  updateWWTLocation(location);
  resetCamera();
  WWTControl.singleton.renderOneFrame();
});

// watch(selectedTime, (value: number) => {
//   console.log(value);});
watch(playing, (play: boolean) => {
  store.setClockSync(play);
});

watch(showAltAzGrid, (show: boolean) => {
  updateAltAzGrid(show);
  updateAltAzGridText(show || showHorizon.value);
});
watch(showEcliptic, updateEcliptic);
watch(showConstellations, updateConstellations);
watch(showHorizon, (show: boolean) => {
  updateAltAzGridText(show || showAltAzGrid.value);
});

watch(dateTime, (dt: Date) => {
  store.setTime(dt);
});

watch(skipIntroChecked, (checked: boolean) => {
  window.localStorage.setItem(SKIP_INTRO_CONTENT_KEY, String(checked));
});

watch(showTextSheet, (show: boolean) => {
  const now = Date.now();
  if (show) {
    infoStartTimestamp = now; 
  } else if (infoStartTimestamp !== null) {
    infoTimeMs += (now - infoStartTimestamp);
    infoStartTimestamp = null;
  }
});

watch(showVideoSheet, (show: boolean) => {
  if (show) {
    videoOpened = true;
  }
});

watch(inNorthernHemisphere, (_inNorth: boolean) => resetAltAzGridText());

watch(showSplashScreen, (show: boolean) => {
  if (!(show || showWebGL2Dialog.value)) {
    inIntro.value = true;
  }
});

watch(inIntro, (intro: boolean) => {
  if (!intro) {
    if (!showSplashScreen.value && responseOptOut.value === null) {
      showPrivacyDialog.value = true;
    }
  }
});

watch(responseOptOut, (optOut: boolean | null) => {
  if (optOut !== null) {
    window.localStorage.setItem(OPT_OUT_KEY, String(optOut));
    if (optOut) {
      clearRatingTimeout();
      showRating.value = false;
    }
  }
});

watch(ratingOptOut, (optOut: boolean | null) => {
  if (optOut !== null) {
    window.localStorage.setItem(RATING_OPT_OUT_KEY, String(optOut));
    if (optOut) {
      clearRatingTimeout();
    }
  }
});

return (_ctx: any,_cache: any) => {
  const _component_WorldWideTelescope = _resolveComponent("WorldWideTelescope")!
  const _component_splash_screen = _resolveComponent("splash-screen")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_icon_button = _resolveComponent("icon-button")!
  const _component_geolocation_button = _resolveComponent("geolocation-button")!
  const _component_location_search = _resolveComponent("location-search")!
  const _component_location_selector = _resolveComponent("location-selector")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_time_display = _resolveComponent("time-display")!
  const _component_date_time_picker = _resolveComponent("date-time-picker")!
  const _component_v_overlay = _resolveComponent("v-overlay")!
  const _component_speed_control = _resolveComponent("speed-control")!
  const _component_credit_logos = _resolveComponent("credit-logos")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_funding_acknowledgement = _resolveComponent("funding-acknowledgement")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_user_experience = _resolveComponent("user-experience")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_cds_privacy_policy = _resolveComponent("cds-privacy-policy")!
  const _component_v_app = _resolveComponent("v-app")!

  return (_openBlock(), _createBlock(_component_v_app, {
    id: "app",
    style: _normalizeStyle(cssVars.value)
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_WorldWideTelescope, { "wwt-namespace": _ctx.wwtNamespace }, null, 8, ["wwt-namespace"]),
        _createVNode(_component_splash_screen, {
          modelValue: showSplashScreen.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showSplashScreen).value = $event)),
          title: "Planetary Parade",
          cssVars: cssVars.value,
          onClose: closeSplashScreen
        }, null, 8, ["modelValue", "cssVars"]),
        _createVNode(_component_v_dialog, {
          modelValue: inIntro.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((inIntro).value = $event))
        }, {
          default: _withCtx(() => [
            (inIntro.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createVNode(_component_v_card, { class: "info-card elevation-5" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_3, [
                        _createVNode(_component_font_awesome_icon, {
                          size: "xl",
                          class: "ma-1",
                          color: accentColor.value,
                          icon: "xmark",
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (inIntro.value = !inIntro.value)),
                          onKeyup: _cache[2] || (_cache[2] = _withKeys(($event: any) => (inIntro.value = !inIntro.value), ["enter"])),
                          tabindex: "0",
                          "tooltip-location": "start"
                        }, null, 8, ["color"])
                      ]),
                      _createElementVNode("div", _hoisted_4, [
                        _cache[75] || (_cache[75] = _createElementVNode("h3", {
                          style: {"color":"#f4ba3e"},
                          class: "mb-2"
                        }, "Quick Start", -1)),
                        _createElementVNode("ol", null, [
                          _createElementVNode("li", null, [
                            _cache[62] || (_cache[62] = _createTextVNode("Set desired location using ")),
                            _createVNode(_component_font_awesome_icon, {
                              class: "bullet-icon",
                              icon: "location-dot",
                              style: {"color":"#f4ba3e"}
                            }),
                            _cache[63] || (_cache[63] = _createTextVNode()),
                            _cache[64] || (_cache[64] = _createElementVNode("strong", null, [
                              _createElementVNode("span", { style: {"color":"#f4ba3e"} }, "(top-center)")
                            ], -1)),
                            _cache[65] || (_cache[65] = _createTextVNode("."))
                          ]),
                          _createElementVNode("li", null, [
                            _cache[66] || (_cache[66] = _createTextVNode("Click box that displays date/time ")),
                            _createElementVNode("strong", null, [
                              _withDirectives(_createElementVNode("span", _hoisted_5, "(bottom-left)", 512), [
                                [_vShow, !_unref(xs)]
                              ]),
                              _withDirectives(_createElementVNode("span", _hoisted_6, "(bottom-center)", 512), [
                                [_vShow, _unref(xs)]
                              ])
                            ]),
                            _cache[67] || (_cache[67] = _createTextVNode(" to update, or press ")),
                            _createVNode(_component_font_awesome_icon, {
                              class: "bullet-icon",
                              icon: "play",
                              style: {"color":"#f4ba3e"}
                            }),
                            _cache[68] || (_cache[68] = _createTextVNode(" to advance time."))
                          ]),
                          _cache[74] || (_cache[74] = _createElementVNode("li", null, "Go outdoors and find the planet parade!", -1)),
                          _createElementVNode("li", null, [
                            _cache[69] || (_cache[69] = _createTextVNode("Learn more using ")),
                            _createVNode(_component_font_awesome_icon, {
                              class: "bullet-icon",
                              icon: "info",
                              style: {"color":"#f4ba3e"}
                            }),
                            _cache[70] || (_cache[70] = _createTextVNode(" and ")),
                            _createVNode(_component_font_awesome_icon, {
                              class: "bullet-icon",
                              icon: "video",
                              style: {"color":"#f4ba3e"}
                            }),
                            _cache[71] || (_cache[71] = _createTextVNode()),
                            _cache[72] || (_cache[72] = _createElementVNode("strong", null, [
                              _createElementVNode("span", { style: {"color":"#f4ba3e"} }, "(upper-left)")
                            ], -1)),
                            _cache[73] || (_cache[73] = _createTextVNode(". "))
                          ])
                        ])
                      ]),
                      _createVNode(_component_v_checkbox, {
                        modelValue: skipIntroChecked.value,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((skipIntroChecked).value = $event)),
                        label: "Do not display Intro Content again",
                        density: "default",
                        "hide-details": ""
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_Transition, { name: "fade" }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", _hoisted_7, _cache[76] || (_cache[76] = [
              _createElementVNode("div", { class: "container" }, [
                _createElementVNode("div", { class: "spinner" }),
                _createElementVNode("p", null, "Loading …")
              ], -1)
            ]), 512), [
              [_vShow, isLoading.value]
            ])
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createVNode(_component_icon_button, {
                modelValue: showLocationSelector.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showLocationSelector).value = $event)),
                "fa-icon": "location-dot",
                color: buttonColor.value,
                "tooltip-text": "Select Location",
                "tooltip-location": "start"
              }, null, 8, ["modelValue", "color"]),
              _createVNode(_component_v_dialog, {
                modelValue: showLocationSelector.value,
                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((showLocationSelector).value = $event)),
                "max-width": "fit-content",
                transition: "slide-y-transition"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card, null, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_11, [
                        _createVNode(_component_font_awesome_icon, {
                          style: {"cursor":"pointer","z-index":"1000"},
                          icon: "xmark",
                          size: "xl",
                          onClick: _cache[6] || (_cache[6] = ($event: any) => (showLocationSelector.value = false)),
                          onKeyup: _cache[7] || (_cache[7] = _withKeys(($event: any) => (showLocationSelector.value = false), ["enter"])),
                          tabindex: "0",
                          color: "black"
                        })
                      ]),
                      _createElementVNode("div", _hoisted_12, [
                        _createVNode(_component_geolocation_button, {
                          id: "location",
                          size: "30px",
                          density: "default",
                          elevation: "5",
                          color: accentColor.value,
                          onGeolocation: _cache[8] || (_cache[8] = ($event: any) => (selectedLocation.value = {longitudeDeg: $event.longitude, latitudeDeg: $event.latitude}))
                        }, null, 8, ["color"]),
                        _createVNode(_component_location_search, {
                          class: _normalizeClass(['location-search']),
                          small: "",
                          "button-size": "xl",
                          "accent-color": accentColor.value,
                          "search-provider": searchProvider,
                          onSetLocation: setLocationFromSearchFeature,
                          onError: _cache[9] || (_cache[9] = ($event: any) => (searchErrorMessage.value = $event))
                        }, null, 8, ["accent-color"])
                      ]),
                      _createVNode(_component_location_selector, {
                        "model-value": selectedLocation.value,
                        "onUpdate:modelValue": updateLocationFromMap
                      }, null, 8, ["model-value"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"]),
              _createElementVNode("div", {
                tabindex: "0",
                id: "my-location-label",
                onClick: _cache[11] || (_cache[11] = ($event: any) => (showLocationSelector.value=true)),
                onKeyup: _cache[12] || (_cache[12] = _withKeys(($event: any) => (showLocationSelector.value=true), ["enter"]))
              }, [
                _cache[77] || (_cache[77] = _createElementVNode("div", null, "View from:", -1)),
                _createElementVNode("div", null, _toDisplayString(selectedLocationText.value != '' ? selectedLocationText.value : 'Cambridge, MA (default)'), 1)
              ], 32)
            ]),
            _createVNode(_component_icon_button, {
              modelValue: showTextSheet.value,
              "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((showTextSheet).value = $event)),
              "fa-icon": "info",
              color: buttonColor.value,
              "tooltip-text": showTextSheet.value ? 'Hide Info' : 'Learn More',
              "tooltip-location": "start"
            }, null, 8, ["modelValue", "color", "tooltip-text"]),
            _createVNode(_component_icon_button, {
              modelValue: showVideoSheet.value,
              "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((showVideoSheet).value = $event)),
              "fa-icon": "video",
              color: buttonColor.value,
              "tooltip-text": "Watch video",
              "tooltip-location": "start"
            }, null, 8, ["modelValue", "color"])
          ]),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _createVNode(_component_font_awesome_icon, {
                  size: "lg",
                  class: "tab-focusable",
                  color: accentColor.value,
                  icon: showControls.value ? `chevron-down` : `gear`,
                  onClick: _cache[15] || (_cache[15] = ($event: any) => (showControls.value = !showControls.value)),
                  onKeyup: _cache[16] || (_cache[16] = _withKeys(($event: any) => (showControls.value = !showControls.value), ["enter"])),
                  tabindex: "0"
                }, null, 8, ["color", "icon"])
              ]),
              (showControls.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                    _createVNode(_component_v_checkbox, {
                      color: accentColor.value,
                      modelValue: showEcliptic.value,
                      "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((showEcliptic).value = $event)),
                      onKeyup: _cache[18] || (_cache[18] = _withKeys(($event: any) => (showEcliptic.value = !showEcliptic.value), ["enter"])),
                      label: "Ecliptic",
                      "hide-details": ""
                    }, null, 8, ["color", "modelValue"]),
                    _createVNode(_component_v_checkbox, {
                      color: accentColor.value,
                      modelValue: showAltAzGrid.value,
                      "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((showAltAzGrid).value = $event)),
                      onKeyup: _cache[20] || (_cache[20] = _withKeys(($event: any) => (showAltAzGrid.value = !showAltAzGrid.value), ["enter"])),
                      label: "Sky Grid",
                      "hide-details": ""
                    }, null, 8, ["color", "modelValue"]),
                    _createVNode(_component_v_checkbox, {
                      color: accentColor.value,
                      modelValue: showHorizon.value,
                      "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((showHorizon).value = $event)),
                      onKeyup: _cache[22] || (_cache[22] = _withKeys(($event: any) => (showHorizon.value = !showHorizon.value), ["enter"])),
                      label: "Horizon/Sky",
                      "hide-details": ""
                    }, null, 8, ["color", "modelValue"]),
                    _createVNode(_component_v_checkbox, {
                      color: accentColor.value,
                      modelValue: showPlanetLabels.value,
                      "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event: any) => ((showPlanetLabels).value = $event)),
                      onKeyup: _cache[24] || (_cache[24] = _withKeys(($event: any) => (showPlanetLabels.value = !showPlanetLabels.value), ["enter"])),
                      label: "Planet Labels",
                      "hide-details": ""
                    }, null, 8, ["color", "modelValue"]),
                    _createVNode(_component_v_checkbox, {
                      color: accentColor.value,
                      modelValue: showConstellations.value,
                      "onUpdate:modelValue": _cache[25] || (_cache[25] = ($event: any) => ((showConstellations).value = $event)),
                      onKeyup: _cache[26] || (_cache[26] = _withKeys(($event: any) => (showConstellations.value = !showConstellations.value), ["enter"])),
                      label: "Constellations",
                      "hide-details": ""
                    }, null, 8, ["color", "modelValue"])
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("div", _hoisted_18, [
                _cache[78] || (_cache[78] = _createElementVNode("div", { class: "planet-visibility-title" }, "Risen", -1)),
                _createVNode(_component_font_awesome_icon, {
                  size: "lg",
                  class: "tab-focusable",
                  color: accentColor.value,
                  icon: showPlanetVisiblity.value ? `chevron-down` : `chevron-up`,
                  onClick: _cache[27] || (_cache[27] = ($event: any) => (showPlanetVisiblity.value = !showPlanetVisiblity.value)),
                  onKeyup: _cache[28] || (_cache[28] = _withKeys(($event: any) => (showPlanetVisiblity.value = !showPlanetVisiblity.value), ["enter"])),
                  tabindex: "0"
                }, null, 8, ["color", "icon"])
              ]),
              (showPlanetVisiblity.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _createElementVNode("p", {
                      class: _normalizeClass(['planet-label','sun', sunVis.value ? '' : 'not-visible' ])
                    }, [
                      _createVNode(_component_v_icon, null, {
                        default: _withCtx(() => _cache[79] || (_cache[79] = [
                          _createTextVNode("mdi-weather-sunny")
                        ])),
                        _: 1
                      }),
                      _cache[80] || (_cache[80] = _createTextVNode(" Sun"))
                    ], 2),
                    _createElementVNode("p", {
                      class: _normalizeClass(['planet-label', mercuryVis.value ? '' : 'not-visible' ])
                    }, [
                      _createVNode(_component_v_icon, null, {
                        default: _withCtx(() => _cache[81] || (_cache[81] = [
                          _createTextVNode("mdi-eye-outline")
                        ])),
                        _: 1
                      }),
                      _cache[82] || (_cache[82] = _createTextVNode(" Mercury"))
                    ], 2),
                    _createElementVNode("p", {
                      class: _normalizeClass(['planet-label', venusVis.value   ? '' : 'not-visible' ])
                    }, [
                      _createVNode(_component_v_icon, null, {
                        default: _withCtx(() => _cache[83] || (_cache[83] = [
                          _createTextVNode("mdi-eye-outline")
                        ])),
                        _: 1
                      }),
                      _cache[84] || (_cache[84] = _createTextVNode(" Venus"))
                    ], 2),
                    _createElementVNode("p", {
                      class: _normalizeClass(['planet-label', marsVis.value    ? '' : 'not-visible' ])
                    }, [
                      _createVNode(_component_v_icon, null, {
                        default: _withCtx(() => _cache[85] || (_cache[85] = [
                          _createTextVNode("mdi-eye-outline")
                        ])),
                        _: 1
                      }),
                      _cache[86] || (_cache[86] = _createTextVNode(" Mars"))
                    ], 2),
                    _createElementVNode("p", {
                      class: _normalizeClass(['planet-label', jupiterVis.value ? '' : 'not-visible' ])
                    }, [
                      _createVNode(_component_v_icon, null, {
                        default: _withCtx(() => _cache[87] || (_cache[87] = [
                          _createTextVNode("mdi-eye-outline")
                        ])),
                        _: 1
                      }),
                      _cache[88] || (_cache[88] = _createTextVNode(" Jupiter"))
                    ], 2),
                    _createElementVNode("p", {
                      class: _normalizeClass(['planet-label', saturnVis.value  ? '' : 'not-visible' ])
                    }, [
                      _createVNode(_component_v_icon, null, {
                        default: _withCtx(() => _cache[89] || (_cache[89] = [
                          _createTextVNode("mdi-eye-outline")
                        ])),
                        _: 1
                      }),
                      _cache[90] || (_cache[90] = _createTextVNode(" Saturn"))
                    ], 2),
                    _createElementVNode("p", {
                      class: _normalizeClass(['planet-label', uranusVis.value  ? '' : 'not-visible' ])
                    }, [
                      _createVNode(_component_v_icon, null, {
                        default: _withCtx(() => _cache[91] || (_cache[91] = [
                          _createTextVNode("mdi-binoculars")
                        ])),
                        _: 1
                      }),
                      _cache[92] || (_cache[92] = _createTextVNode(" Uranus"))
                    ], 2),
                    _createElementVNode("p", {
                      class: _normalizeClass(['planet-label', neptuneVis.value ? '' : 'not-visible' ])
                    }, [
                      _createVNode(_component_v_icon, null, {
                        default: _withCtx(() => _cache[93] || (_cache[93] = [
                          _createTextVNode("mdi-telescope")
                        ])),
                        _: 1
                      }),
                      _cache[94] || (_cache[94] = _createTextVNode(" Neptune"))
                    ], 2)
                  ]))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_20, [
          _createElementVNode("div", _hoisted_21, [
            _createVNode(_component_v_overlay, {
              modelValue: datePickerOpen.value,
              "onUpdate:modelValue": _cache[33] || (_cache[33] = ($event: any) => ((datePickerOpen).value = $event)),
              activator: "parent",
              "location-strategy": "connected",
              location: "top end",
              origin: "bottom end",
              scrim: false,
              style: _normalizeStyle(cssVars.value)
            }, {
              activator: _withCtx(({props}) => [
                _createVNode(_component_v_card, _mergeProps(props, {
                  class: "td__card",
                  width: "fit-content",
                  rounded: "lg",
                  tabindex: "0",
                  onKeyup: _withKeys(props.onClick, ["enter"])
                }), {
                  default: _withCtx(() => [
                    _createVNode(_component_time_display, {
                      class: "bsn__time",
                      date: localSelectedDate.value,
                      ampm: "",
                      "short-time-date": true,
                      "show-timezone": "",
                      timezone: _unref(shortTimezone)
                    }, null, 8, ["date", "timezone"]),
                    (!(_unref(smAndDown) || mobile.value))
                      ? (_openBlock(), _createBlock(_component_v_icon, {
                          key: 0,
                          class: "td__icon"
                        }, {
                          default: _withCtx(() => _cache[95] || (_cache[95] = [
                            _createTextVNode("mdi-cursor-default-click")
                          ])),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1040, ["onKeyup"])
              ]),
              default: _withCtx(() => [
                _createVNode(_component_v_card, {
                  ref: "dtpCard",
                  tabindex: "0",
                  width: "fit-content",
                  elevation: "5"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      tabindex: "0",
                      class: "dtp-close-button",
                      onClick: _cache[29] || (_cache[29] = ($event: any) => (datePickerOpen.value=false)),
                      onKeyup: _cache[30] || (_cache[30] = _withKeys(($event: any) => (datePickerOpen.value=false), ["enter"])),
                      color: accentColor.value,
                      size: "18"
                    }, {
                      default: _withCtx(() => _cache[96] || (_cache[96] = [
                        _createTextVNode("mdi-close")
                      ])),
                      _: 1
                    }, 8, ["color"]),
                    _createVNode(_component_date_time_picker, {
                      tabindex: "0",
                      modelValue: localSelectedDate.value,
                      "onUpdate:modelValue": _cache[32] || (_cache[32] = ($event: any) => ((localSelectedDate).value = $event)),
                      "editable-time": true
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("button", {
                          class: "dtp__button",
                          onClick: _cache[31] || (_cache[31] = () => {selectedTime.value = Date.now()}),
                          name: "set-now",
                          "aria-label": "Set time to Now"
                        }, "Now")
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                }, 512)
              ]),
              _: 1
            }, 8, ["modelValue", "style"])
          ]),
          _createVNode(_component_speed_control, {
            playing: playing.value,
            "onUpdate:playing": [
              _cache[34] || (_cache[34] = ($event: any) => ((playing).value = $event)),
              _cache[37] || (_cache[37] = (_playing) => {
          _unref(wwtStats).playPauseCount += 1;
        })
            ],
            store: _unref(store),
            color: accentColor.value,
            defaultRate: 1000,
            useInline: _unref(xs),
            maxSpeed: 10000,
            "show-text": "",
            onReset: _cache[35] || (_cache[35] = () => {
          selectedTime.value = Date.now();
          const altTime = _unref(getTimeforSunAlt)(2).setting;
          if (altTime) {
            selectedTime.value = altTime;
            nextTick(() => {
            resetCamera(new Date(altTime));
            });
          }
          _unref(wwtStats).timeResetCount += 1;
        }),
            "onUpdate:reverse": _cache[36] || (_cache[36] = (_reverse) => {
          _unref(wwtStats).reverseCount += 1;
        }),
            onSlowDown: _cache[38] || (_cache[38] = (rate) => {
          _unref(wwtStats).slowdowns.push(rate);
        }),
            onSpeedUp: _cache[39] || (_cache[39] = (rate) => {
          _unref(wwtStats).speedups.push(rate);
        }),
            onSetRate: _cache[40] || (_cache[40] = (rate) => {
          _unref(wwtStats).rateSelections.push(rate);
        })
          }, null, 8, ["playing", "store", "color", "useInline"]),
          _createElementVNode("div", _hoisted_22, [
            _createVNode(_component_icon_button, {
              "md-icon": "mdi-information-outline",
              onActivate: _cache[41] || (_cache[41] = () => inIntro.value = true),
              color: accentColor.value,
              "focus-color": accentColor.value,
              "tooltip-text": "Show Quick Start Guide",
              "tooltip-location": "bottom",
              "tooltip-offset": "5px",
              "show-tooltip": !mobile.value,
              mdSize: "1.2em"
            }, null, 8, ["color", "focus-color", "show-tooltip"]),
            _createVNode(_component_icon_button, {
              "md-icon": "mdi-lock",
              onActivate: _cache[42] || (_cache[42] = () => showPrivacyDialog.value = true),
              color: accentColor.value,
              "focus-color": accentColor.value,
              "tooltip-text": "Change privacy settings",
              "tooltip-location": "bottom",
              "tooltip-offset": "5px",
              "show-tooltip": !mobile.value,
              mdSize: "1.2em"
            }, null, 8, ["color", "focus-color", "show-tooltip"])
          ]),
          (!smallSize.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                _createVNode(_component_credit_logos, { "default-logos": ['cosmicds', 'wwt', 'sciact', 'nasa'] })
              ]))
            : _createCommentVNode("", true)
        ]),
        _createVNode(_component_v_dialog, {
          class: "error-dialog",
          style: _normalizeStyle(cssVars.value),
          modelValue: showWebGL2Dialog.value,
          "onUpdate:modelValue": _cache[43] || (_cache[43] = ($event: any) => ((showWebGL2Dialog).value = $event)),
          persistent: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, null, {
              default: _withCtx(() => _cache[97] || (_cache[97] = [
                _createElementVNode("div", { class: "error-message" }, [
                  _createElementVNode("p", null, [
                    _createElementVNode("strong", null, "This app requires WebGL 2")
                  ]),
                  _createElementVNode("p", { class: "mt-2" }, " Check your browser's settings and enable WebGL 2 (\"graphics acceleration\" on some browsers). "),
                  _createElementVNode("p", { class: "mt-2" }, [
                    _createTextVNode(" You can check whether your browser supports WebGL 2 and get assistance "),
                    _createElementVNode("a", {
                      href: "https://get.webgl.org/webgl2/",
                      target: "_blank",
                      rel: "noopener noreferrer"
                    }, "here"),
                    _createTextVNode(". ")
                  ])
                ], -1)
              ])),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["style", "modelValue"]),
        _createVNode(_component_v_dialog, {
          id: "video-container",
          modelValue: showVideoSheet.value,
          "onUpdate:modelValue": _cache[46] || (_cache[46] = ($event: any) => ((showVideoSheet).value = $event)),
          transition: "slide-y-transition",
          fullscreen: ""
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_24, [
              _createVNode(_component_font_awesome_icon, {
                id: "video-close-icon",
                class: "close-icon",
                icon: "times",
                size: "lg",
                onClick: _cache[44] || (_cache[44] = ($event: any) => (showVideoSheet.value = false)),
                onKeyup: _cache[45] || (_cache[45] = _withKeys(($event: any) => (showVideoSheet.value = false), ["enter"])),
                tabindex: "0"
              }),
              _cache[98] || (_cache[98] = _createElementVNode("video", {
                controls: "",
                id: "info-video"
              }, [
                _createElementVNode("source", {
                  src: "CosmicDS Planet Parade Overhead View.mp4",
                  type: "video/mp4"
                })
              ], -1))
            ])
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_v_dialog, {
          scrim: "false",
          modelValue: showPrivacyDialog.value,
          "onUpdate:modelValue": _cache[49] || (_cache[49] = ($event: any) => ((showPrivacyDialog).value = $event)),
          "max-width": "400px",
          id: "privacy-popup-dialog"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_text, null, {
                  default: _withCtx(() => _cache[99] || (_cache[99] = [
                    _createTextVNode(" To evaluate usage of this app, "),
                    _createElementVNode("strong", null, "anonymized", -1),
                    _createTextVNode(" data may be collected, including locations searched or selected on map. Places selected via geolocation services on your device are NOT collected. ")
                  ])),
                  _: 1
                }),
                _createVNode(_component_v_card_actions, { class: "pt-3" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_spacer),
                    _createVNode(_component_v_btn, {
                      color: "#BDBDBD",
                      href: "https://www.cfa.harvard.edu/privacy-statement",
                      target: "_blank",
                      rel: "noopener noreferrer"
                    }, {
                      default: _withCtx(() => _cache[100] || (_cache[100] = [
                        _createTextVNode(" Privacy Policy ")
                      ])),
                      _: 1
                    }),
                    _createVNode(_component_v_btn, {
                      color: "#ff6666",
                      onClick: _cache[47] || (_cache[47] = () => {
              responseOptOut.value = true;
              showPrivacyDialog.value = false;
            })
                    }, {
                      default: _withCtx(() => _cache[101] || (_cache[101] = [
                        _createTextVNode(" Opt out ")
                      ])),
                      _: 1
                    }),
                    _createVNode(_component_v_btn, {
                      color: "green",
                      onClick: _cache[48] || (_cache[48] = () => {
              responseOptOut.value = false;
              showPrivacyDialog.value = false;
            })
                    }, {
                      default: _withCtx(() => _cache[102] || (_cache[102] = [
                        _createTextVNode(" Allow ")
                      ])),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_v_dialog, {
          style: _normalizeStyle(cssVars.value),
          class: "bottom-sheet",
          id: "text-bottom-sheet",
          "hide-overlay": "",
          persistent: "",
          "no-click-animation": "",
          absolute: "",
          width: "100%",
          scrim: false,
          location: "bottom",
          modelValue: showTextSheet.value,
          "onUpdate:modelValue": _cache[54] || (_cache[54] = ($event: any) => ((showTextSheet).value = $event)),
          transition: "dialog-bottom-transition",
          "retain-focus": !showRating.value
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, { height: "100%" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_tabs, {
                  modelValue: tab.value,
                  "onUpdate:modelValue": _cache[50] || (_cache[50] = ($event: any) => ((tab).value = $event)),
                  height: "32px",
                  color: accentColor.value,
                  "slider-color": accentColor.value,
                  id: "tabs",
                  dense: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_tab, {
                      class: "info-tabs",
                      tabindex: "0"
                    }, {
                      default: _withCtx(() => _cache[103] || (_cache[103] = [
                        _createElementVNode("h3", null, "Information", -1)
                      ])),
                      _: 1
                    }),
                    _createVNode(_component_v_tab, {
                      class: "info-tabs",
                      tabindex: "0"
                    }, {
                      default: _withCtx(() => _cache[104] || (_cache[104] = [
                        _createElementVNode("h3", null, "Using WWT", -1)
                      ])),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue", "color", "slider-color"]),
                _createVNode(_component_font_awesome_icon, {
                  id: "close-text-icon",
                  class: "control-icon",
                  icon: "times",
                  size: "lg",
                  onClick: _cache[51] || (_cache[51] = ($event: any) => (showTextSheet.value = false)),
                  onKeyup: _cache[52] || (_cache[52] = _withKeys(($event: any) => (showTextSheet.value = false), ["enter"])),
                  tabindex: "0"
                }),
                _createVNode(_component_v_window, {
                  modelValue: tab.value,
                  "onUpdate:modelValue": _cache[53] || (_cache[53] = ($event: any) => ((tab).value = $event)),
                  id: "tab-items",
                  class: "pb-2 no-bottom-border-radius"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_window_item, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, { class: "no-bottom-border-radius scrollable" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, { class: "info-text no-bottom-border-radius" }, {
                              default: _withCtx(() => [
                                _cache[112] || (_cache[112] = _createElementVNode("h3", null, "What is a planet parade?", -1)),
                                _cache[113] || (_cache[113] = _createElementVNode("p", null, " A planet parade occurs when four or more planets are visible in the night sky at once. Even more rare and notable are parades where six, or even all seven other planets—Mercury, Venus, Mars, Jupiter, Saturn, Uranus, and Neptune—are visible in the sky together. The most recent seven-planet parade occurred in February 2025. The next six-planet parade will occur in February 2026. ", -1)),
                                _cache[114] || (_cache[114] = _createElementVNode("p", null, " Which planets will be visible to your eye depends on how dark your night sky is. Starting around mid-February 2026, from a reasonably dark, clear sky, you should be able to see four of the six planets (Venus, Mercury, Saturn, and Jupiter) by eye. Neptune and Uranus will also be up, but you will likely need binoculars or a small telescope to see them. ", -1)),
                                _cache[115] || (_cache[115] = _createElementVNode("h3", null, "Cool! How do I see a planet parade?", -1)),
                                _cache[116] || (_cache[116] = _createElementVNode("p", null, " You can use this resource to see which planets are visible in the sky on a given date. ", -1)),
                                _createElementVNode("ul", null, [
                                  _createElementVNode("li", null, [
                                    _cache[105] || (_cache[105] = _createTextVNode("Click ")),
                                    _createVNode(_component_font_awesome_icon, {
                                      class: "bullet-icon",
                                      icon: "location-dot"
                                    }),
                                    _cache[106] || (_cache[106] = _createTextVNode(" in the top-center of the view and choose your location. (The default location is Cambridge, MA.)"))
                                  ]),
                                  _cache[107] || (_cache[107] = _createElementVNode("li", null, "The display defaults to the current day just before sunset. Use the time controls to advance time until just after sunset.", -1)),
                                  _cache[108] || (_cache[108] = _createElementVNode("li", null, [
                                    _createTextVNode(" If "),
                                    _createElementVNode("span", { style: {"color":"var(--accent-color)"} }, "Horizon/Sky"),
                                    _createTextVNode(" is checked, you can see the Sun rise above the horizon in the morning and set in the evening. The sky will lighten and darken with the Sun's changing position. ")
                                  ], -1)),
                                  _cache[109] || (_cache[109] = _createElementVNode("li", null, [
                                    _createTextVNode(" Check "),
                                    _createElementVNode("span", { style: {"color":"var(--accent-color)"} }, "Sky Grid"),
                                    _createTextVNode(" to display the cardinal directions and/or check "),
                                    _createElementVNode("span", { style: {"color":"var(--accent-color)"} }, "Constellations"),
                                    _createTextVNode(" to help orient yourself . ")
                                  ], -1))
                                ]),
                                _cache[117] || (_cache[117] = _createElementVNode("h3", null, "How do I find my way around the sky?", -1)),
                                _cache[118] || (_cache[118] = _createElementVNode("p", null, " Here are some tips for finding the planets in the sky. ", -1)),
                                _cache[119] || (_cache[119] = _createElementVNode("ul", null, [
                                  _createElementVNode("li", null, " Venus, Jupiter, Saturn, and Mars are the brightest planets and usually the easiest to spot. "),
                                  _createElementVNode("li", null, " Depending on where Venus and Mercury are in their orbits around the Sun, they will either be visible just after sunset (if they are \"following\" the Sun) or just before sunrise (if they are \"ahead of\" the Sun). "),
                                  _createElementVNode("li", null, " If Venus is \"following\" the Sun and visible in the evening, you can find it just above the western horizon for a short time after sunset. (If it is \"ahead of\" the Sun and visible in the morning before sunrise, you can see it above the eastern horizon.) "),
                                  _createElementVNode("li", null, [
                                    _createTextVNode(" If "),
                                    _createElementVNode("span", { style: {"color":"var(--accent-color)"} }, "Ecliptic"),
                                    _createTextVNode(" is checked, you can see a magenta arc that passes through all the planets. The ecliptic is the path that the Sun appears to follow over the course of a year. All the planets (and the Moon) also move roughly along the ecliptic. Imagine that arc in your sky to find all the planets that are above the horizon. ")
                                  ])
                                ], -1)),
                                _cache[120] || (_cache[120] = _createElementVNode("h3", null, "What is the significance of a planet parade?", -1)),
                                _cache[121] || (_cache[121] = _createElementVNode("p", null, " A seven-planet parade happens when all the planets happen to be on the same side of their orbits around the Sun as Earth, so they all are on Earth's night-time side. (When a planet is on the opposite side of the Sun from Earth, that means it is up in our sky during the day when the Sun outshines it.) ", -1)),
                                _cache[122] || (_cache[122] = _createElementVNode("p", null, [
                                  _createElementVNode("strong", null, "The planets are not really all in a line."),
                                  _createTextVNode(" It only appears that way from Earth because Earth and the other planets all orbit the Sun in roughly the same plane (as if they were on a vinyl record). From Earth's point of view, all the other solar system objects appear to move along a circular path around the sky, which ancient astronomers called the \"ecliptic.\" ")
                                ], -1)),
                                _createElementVNode("p", null, [
                                  _cache[110] || (_cache[110] = _createTextVNode(" Click ")),
                                  _createVNode(_component_font_awesome_icon, {
                                    class: "bullet-icon",
                                    icon: "video",
                                    style: {"color":"#f4ba3e"}
                                  }),
                                  _cache[111] || (_cache[111] = _createTextVNode(" in the upper left to watch a (silent) video showing an overhead view of the planets and how their positions lead to the planet parade. "))
                                ]),
                                _cache[123] || (_cache[123] = _createElementVNode("h3", null, "What else can I do to learn more?", -1)),
                                _cache[124] || (_cache[124] = _createElementVNode("p", null, [
                                  _createTextVNode(" Take your own images of the planets using "),
                                  _createElementVNode("strong", null, [
                                    _createElementVNode("a", {
                                      href: "https://mo-www.cfa.harvard.edu/OWN/todays_solar_system.html#SS",
                                      target: "_blank",
                                      rel: "noopener noreferrer"
                                    }, "MicroObservatory's Observing With NASA")
                                  ]),
                                  _createTextVNode(" program! ")
                                ], -1)),
                                _cache[125] || (_cache[125] = _createElementVNode("h3", null, "Will the planets be this big in my sky?", -1)),
                                _cache[126] || (_cache[126] = _createElementVNode("p", null, " Unfortunately, no. In this visualization, we scaled up the size of the solar system objects so you would be able to see them when zoomed out to a large swath of sky. In reality, the planets will appear in the sky as points of light (or very small disks through binoculars or a telescope). ", -1)),
                                _createVNode(_component_v_spacer, { class: "end-spacer" })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_window_item, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, { class: "no-bottom-border-radius scrollable" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, { class: "info-text no-bottom-border-radius" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_container, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_row, { align: "center" }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "4" }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_chip, {
                                              label: "",
                                              outlined: ""
                                            }, {
                                              default: _withCtx(() => _cache[127] || (_cache[127] = [
                                                _createTextVNode(" Pan ")
                                              ])),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_v_col, {
                                          cols: "8",
                                          class: "pt-1"
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "press + drag" : "click + drag"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? ":" : "or") + " ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? ":" : "W-A-S-D"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? ":" : "keys"), 1),
                                            _cache[128] || (_cache[128] = _createElementVNode("br", null, null, -1))
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, { align: "center" }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "4" }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_chip, {
                                              label: "",
                                              outlined: ""
                                            }, {
                                              default: _withCtx(() => _cache[129] || (_cache[129] = [
                                                _createTextVNode(" Zoom ")
                                              ])),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_v_col, {
                                          cols: "8",
                                          class: "pt-1"
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "pinch in and out" : "scroll in and out"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? ":" : "or") + " ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? ":" : "I-O"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? ":" : "keys"), 1),
                                            _cache[130] || (_cache[130] = _createElementVNode("br", null, null, -1))
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "12" }, {
                                          default: _withCtx(() => [
                                            _cache[131] || (_cache[131] = _createElementVNode("div", { class: "credits" }, [
                                              _createElementVNode("h3", null, "Credits:"),
                                              _createElementVNode("h4", null, [
                                                _createElementVNode("a", {
                                                  href: "https://www.cosmicds.cfa.harvard.edu/",
                                                  target: "_blank",
                                                  rel: "noopener noreferrer"
                                                }, "CosmicDS"),
                                                _createTextVNode(" Vue Data Stories Team:")
                                              ]),
                                              _createTextVNode(" Jon Carifio"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Pat Udomprasert"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" John Lewis"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Alyssa Goodman"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Mary Dussault"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Harry Houghton"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Evaluator: Sue Sunbury"),
                                              _createElementVNode("br"),
                                              _createElementVNode("br"),
                                              _createElementVNode("h4", null, "WorldWide Telescope Team:"),
                                              _createTextVNode(" Peter Williams"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" A. David Weigel"),
                                              _createElementVNode("br"),
                                              _createTextVNode(" Jon Carifio"),
                                              _createElementVNode("br")
                                            ], -1)),
                                            _createVNode(_component_v_spacer, { class: "end-spacer" })
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, null, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_funding_acknowledgement)
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["style", "modelValue", "retain-focus"]),
        _createVNode(_component_v_container, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_expand_transition, null, {
              default: _withCtx(() => [
                _withDirectives(_createVNode(_component_user_experience, {
                  question: _unref(question),
                  "icon-size": "3x",
                  onDismiss: _cache[58] || (_cache[58] = (_rating, _comments) => {
          showRating.value = false;
        }),
                  onRating: _cache[59] || (_cache[59] = (rating) => {
          currentRating.value = rating;
          updateUserExperienceInfo(currentRating.value, currentComments.value);
        }),
                  onFinish: _cache[60] || (_cache[60] = (rating, comments) => {
          currentRating.value = rating;
          currentComments.value = comments;
          updateUserExperienceInfo(currentRating.value, currentComments.value)
          showRating.value = false;
        })
                }, {
                  footer: _withCtx(() => [
                    _createElementVNode("div", _hoisted_25, [
                      _createVNode(_component_v_btn, {
                        class: "rating-opt-put",
                        color: "#BDBDBD",
                        size: "small",
                        variant: "text",
                        onClick: _cache[55] || (_cache[55] = () => {
                showRating.value = false;
                ratingOptOut.value = true;
              })
                      }, {
                        default: _withCtx(() => _cache[132] || (_cache[132] = [
                          _createTextVNode(" Don't show again ")
                        ])),
                        _: 1
                      }),
                      _createVNode(_component_v_btn, {
                        class: "privacy-button",
                        color: "#BDBDBD",
                        onClick: _cache[56] || (_cache[56] = ($event: any) => (showRatingPrivacyPolicy.value = true)),
                        onKeyup: _cache[57] || (_cache[57] = _withKeys(($event: any) => (showRatingPrivacyPolicy.value = true), ["enter"])),
                        size: "small",
                        target: "_blank",
                        rel: "noopener noreferrer"
                      }, {
                        default: _withCtx(() => _cache[133] || (_cache[133] = [
                          _createTextVNode(" What is this? ")
                        ])),
                        _: 1
                      })
                    ])
                  ]),
                  _: 1
                }, 8, ["question"]), [
                  [_vShow, showRating.value]
                ])
              ]),
              _: 1
            }),
            _createVNode(_component_cds_privacy_policy, {
              modelValue: showRatingPrivacyPolicy.value,
              "onUpdate:modelValue": _cache[61] || (_cache[61] = ($event: any) => ((showRatingPrivacyPolicy).value = $event))
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }, 8, ["style"]))
}
}

})